import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, Package, Link2, Users, BarChart3, AlertTriangle,
  Pencil, Plus, Loader2, Search, ExternalLink, Trash2, GraduationCap,
  LogOut, Star, MapPin, Mail, Phone, ArrowRight,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// ── Login Screen ──
function VendorLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${BACKEND}/api/vendor-portal/auth/login`, { email });
      onLogin(res.data.token, res.data.vendor, res.data.user);
    } catch (err) { setError(err.response?.data?.detail || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/assets/logo.png" alt="LodgiFlow" className="h-8 w-8 mx-auto mb-4" />
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Vendor Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your products, integrations, and contacts</p>
        </div>
        <form onSubmit={login} className="bg-white rounded-2xl border shadow-lg p-6 space-y-4">
          <div><Label className="text-xs">Email Address</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@vendor.com" autoFocus className="mt-1" /></div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading || !email}>{loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Sign In</Button>
          <p className="text-[10px] text-center text-muted-foreground">Don't have access? Contact your LodgiFlow administrator.</p>
        </form>
      </div>
    </div>
  );
}

// ── Main Portal ──
export default function VendorPortalApp() {
  const [token, setToken] = useState(localStorage.getItem('vp_token') || '');
  const [vendor, setVendor] = useState(null);
  const [tab, setTab] = useState('dashboard');

  const handleLogin = (t, v, u) => {
    setToken(t); setVendor(v);
    localStorage.setItem('vp_token', t);
  };

  const logout = () => {
    setToken(''); setVendor(null);
    localStorage.removeItem('vp_token');
  };

  if (!token) return <VendorLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="bg-white border-b px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="" className="h-5 w-5" />
          <span className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>Vendor Portal</span>
          {vendor && <><Separator orientation="vertical" className="h-4" /><span className="text-xs text-muted-foreground">{vendor.name}</span></>}
        </div>
        <div className="flex items-center gap-1">
          {['dashboard', 'products', 'integrations', 'contacts', 'training'].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${tab === t ? 'bg-primary/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
          <Button variant="ghost" size="icon" className="h-7 w-7 ml-2" onClick={logout}><LogOut className="h-3.5 w-3.5" /></Button>
        </div>
      </nav>
      {/* Content */}
      <div className="max-w-5xl mx-auto">
        {tab === 'dashboard' && <DashboardTab token={token} />}
        {tab === 'products' && <ProductsTab token={token} />}
        {tab === 'integrations' && <IntegrationsTab token={token} />}
        {tab === 'contacts' && <ContactsTab token={token} />}
        {tab === 'training' && <TrainingTab token={token} />}
      </div>
    </div>
  );
}

// ── Dashboard ──
function DashboardTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/vendor-portal/dashboard`, { headers }).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.scores || {};
  return (
    <div className="p-6 space-y-6">
      <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.vendor?.name}</h2>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 rounded-lg border bg-white text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{(data?.products || []).length}</div><div className="text-[10px] text-muted-foreground">Products</div></div>
        <div className="p-4 rounded-lg border bg-white text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.deployment_count || 0}</div><div className="text-[10px] text-muted-foreground">Deployments</div></div>
        <div className="p-4 rounded-lg border bg-white text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{data?.property_count || 0}</div><div className="text-[10px] text-muted-foreground">Properties</div></div>
        <div className="p-4 rounded-lg border bg-white text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{(data?.integrations || []).length}</div><div className="text-[10px] text-muted-foreground">Integrations</div></div>
      </div>

      {s && (s.support_quality || s.reliability) && (
        <div className="p-4 rounded-lg border bg-white">
          <h3 className="text-sm font-semibold mb-3">Vendor Scorecard</h3>
          <div className="grid grid-cols-5 gap-3">
            {[['Support', s.support_quality], ['Integration', s.integration_ease], ['Value', s.value_for_money], ['Reliability', s.reliability], ['Innovation', s.innovation]].map(([label, val]) => (
              <div key={label} className="text-center"><div className="text-lg font-bold" style={{ color: val >= 4 ? '#16a34a' : val >= 3 ? '#d97706' : '#dc2626' }}>{val || '-'}</div><div className="text-[9px] text-muted-foreground">{label}</div></div>
            ))}
          </div>
        </div>
      )}

      {(data?.deployments || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Deployment Footprint</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">{data.deployments.slice(0, 12).map((d, i) => (
            <div key={i} className="p-2.5 rounded-lg border bg-white text-xs"><span className="font-medium">{d.property_name}</span><div className="text-muted-foreground mt-0.5">{d.platform_name} — {d.product_name}</div></div>
          ))}</div>
        </div>
      )}

      {(data?.incidents || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Recent Incidents</h3>
          <div className="space-y-1">{data.incidents.slice(0, 5).map((inc, i) => (
            <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-white text-xs">
              <Badge className={`text-[8px] ${inc.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{inc.severity}</Badge>
              <span className="font-medium flex-1">{inc.title}</span>
              <Badge variant="outline" className="text-[8px]">{inc.status}</Badge>
            </div>
          ))}</div>
        </div>
      )}
    </div>
  );
}

// ── Products ──
function ProductsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = useCallback(() => {
    axios.get(`${BACKEND}/api/vendor-portal/dashboard`, { headers }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetch(); }, [fetch]);

  const openEdit = (p) => { setEditProduct(p); setForm({ name: p.name, description: p.description || '', website: p.website || '', support_url: p.support_url || '', documentation_url: p.documentation_url || '', demo_url: p.demo_url || '', pricing_url: p.pricing_url || '', key_features: p.key_features || '' }); setEditOpen(true); };

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${BACKEND}/api/vendor-portal/products/${editProduct.id}`, form, { headers }); toast.success('Product updated'); setEditOpen(false); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 space-y-4">
      <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Your Products</h2>
      <div className="space-y-3">
        {(data?.products || []).map(p => (
          <div key={p.id} className="p-4 rounded-lg border bg-white">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-sm">{p.name}</h3>
              <Button variant="outline" size="sm" className="gap-1 text-xs h-7" onClick={() => openEdit(p)}><Pencil className="h-2.5 w-2.5" /> Edit</Button>
            </div>
            {p.description && <p className="text-xs text-muted-foreground mb-2">{p.description}</p>}
            <div className="flex flex-wrap gap-2 text-xs">
              {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Website</a>}
              {p.support_url && <a href={p.support_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Support</a>}
              {p.documentation_url && <a href={p.documentation_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="h-3 w-3" /> Docs</a>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={editOpen} onOpenChange={setEditOpen}><DialogContent className="max-w-lg max-h-[85vh] overflow-auto"><DialogHeader><DialogTitle>Edit Product — {editProduct?.name}</DialogTitle></DialogHeader><div className="space-y-3 py-2">
        <div><Label className="text-xs">Product Name</Label><Input value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
        <div><Label className="text-xs">Description</Label><Textarea value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1 min-h-[80px]" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Website</Label><Input value={form.website || ''} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." className="mt-1 text-xs" /></div>
          <div><Label className="text-xs">Support URL</Label><Input value={form.support_url || ''} onChange={e => setForm(p => ({ ...p, support_url: e.target.value }))} placeholder="https://support..." className="mt-1 text-xs" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Documentation URL</Label><Input value={form.documentation_url || ''} onChange={e => setForm(p => ({ ...p, documentation_url: e.target.value }))} placeholder="https://docs..." className="mt-1 text-xs" /></div>
          <div><Label className="text-xs">Demo URL</Label><Input value={form.demo_url || ''} onChange={e => setForm(p => ({ ...p, demo_url: e.target.value }))} placeholder="https://demo..." className="mt-1 text-xs" /></div>
        </div>
        <div><Label className="text-xs">Pricing Page URL</Label><Input value={form.pricing_url || ''} onChange={e => setForm(p => ({ ...p, pricing_url: e.target.value }))} placeholder="https://pricing..." className="mt-1 text-xs" /></div>
        <div><Label className="text-xs">Key Features</Label><Textarea value={form.key_features || ''} onChange={e => setForm(p => ({ ...p, key_features: e.target.value }))} placeholder="Cloud-based PMS with mobile check-in, digital key..." className="mt-1 min-h-[60px]" /></div>
      </div><DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}

// ── Integrations ──
function IntegrationsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [form, setForm] = useState({ product_id: '', partner_product_id: '', partner_vendor_name: '', partner_product_name: '', direction: 'two-way', status: 'active', label: '' });
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('existing'); // 'existing' or 'new'
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = useCallback(() => {
    axios.get(`${BACKEND}/api/vendor-portal/dashboard`, { headers }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetch(); }, [fetch]);

  const searchCatalog = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    try { const r = await axios.get(`${BACKEND}/api/vendor-portal/catalog/products?q=${encodeURIComponent(q)}`, { headers }); setSearchResults(r.data.products || []); }
    catch {}
  }, [headers]);

  useEffect(() => { const t = setTimeout(() => searchCatalog(searchQ), 300); return () => clearTimeout(t); }, [searchQ, searchCatalog]);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND}/api/vendor-portal/integrations`, form, { headers });
      if (res.data.status === 'pending_approval') toast.success('Submitted for admin approval!');
      else toast.success('Integration added!');
      setAddOpen(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Integrations</h2>
        <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> Add Integration</Button>
      </div>
      <div className="space-y-2">
        {(data?.integrations || []).length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No integrations listed yet.</p> :
          (data?.integrations || []).map((integ, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-white text-sm">
              <span className="font-medium">{integ.from_product_name}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium">{integ.to_product_name}</span>
              <Badge variant="outline" className="text-[8px] ml-auto">{integ.direction}</Badge>
              <Badge className={`text-[8px] ${integ.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>{integ.status}</Badge>
            </div>
          ))
        }
      </div>
      <Dialog open={addOpen} onOpenChange={setAddOpen}><DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Add Integration</DialogTitle></DialogHeader><div className="space-y-4 py-2">
        <div><Label className="text-xs">Your Product</Label>
          <Select value={form.product_id || '__none__'} onValueChange={v => setForm(p => ({ ...p, product_id: v === '__none__' ? '' : v }))}><SelectTrigger className="mt-1"><SelectValue placeholder="Select your product..." /></SelectTrigger><SelectContent>{(data?.products || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
        </div>
        <div className="flex gap-1 bg-muted/60 rounded-lg p-0.5 w-fit">
          <button onClick={() => setMode('existing')} className={`px-3 py-1 rounded-md text-xs font-medium ${mode === 'existing' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>From Catalog</button>
          <button onClick={() => setMode('new')} className={`px-3 py-1 rounded-md text-xs font-medium ${mode === 'new' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}>New Partner</button>
        </div>
        {mode === 'existing' ? (
          <div>
            <Label className="text-xs">Search Products</Label>
            <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Type to search..." className="mt-1" />
            {searchResults.length > 0 && (
              <div className="mt-1 max-h-[150px] overflow-y-auto border rounded-lg">
                {searchResults.map(p => (
                  <button key={p.id} onClick={() => { setForm(prev => ({ ...prev, partner_product_id: p.id })); setSearchQ(p.name); setSearchResults([]); }}
                    className="w-full text-left p-2 hover:bg-muted/40 text-sm border-b last:border-0">
                    <span className="font-medium">{p.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">{p.vendor_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label className="text-xs">Vendor Name</Label><Input value={form.partner_vendor_name} onChange={e => setForm(p => ({ ...p, partner_vendor_name: e.target.value }))} placeholder="Partner vendor name" className="mt-1" /></div>
            <div><Label className="text-xs">Product Name</Label><Input value={form.partner_product_name} onChange={e => setForm(p => ({ ...p, partner_product_name: e.target.value }))} placeholder="Partner product name" className="mt-1" /></div>
            <p className="text-[10px] text-muted-foreground">New vendors/products will be submitted for admin approval before appearing in the catalog.</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Direction</Label><Select value={form.direction} onValueChange={v => setForm(p => ({ ...p, direction: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="two-way">Two-Way</SelectItem><SelectItem value="one-way">One-Way</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Status</Label><Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="possible">Possible</SelectItem><SelectItem value="planned">Planned</SelectItem></SelectContent></Select></div>
        </div>
        <div><Label className="text-xs">Label</Label><Input value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g., Room Charges, Guest Profiles" className="mt-1" /></div>
      </div><DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={submit} disabled={saving || !form.product_id || (!form.partner_product_id && !form.partner_vendor_name)}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{mode === 'new' ? 'Submit for Approval' : 'Add Integration'}</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}

// ── Contacts ──
function ContactsTab({ token }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '' });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = useCallback(() => {
    axios.get(`${BACKEND}/api/vendor-portal/contacts`, { headers }).then(r => setContacts(r.data.contacts || [])).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    setSaving(true);
    try { await axios.post(`${BACKEND}/api/vendor-portal/contacts`, form, { headers }); toast.success('Contact added'); setAddOpen(false); setForm({ name: '', email: '', phone: '', role: '' }); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const del = async (id) => { try { await axios.delete(`${BACKEND}/api/vendor-portal/contacts/${id}`, { headers }); toast.success('Deleted'); fetch(); } catch {} };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between"><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Contacts</h2><Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> Add Contact</Button></div>
      <div className="space-y-2">{contacts.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">No contacts added yet.</p> : contacts.map(c => (
        <div key={c.id} className="flex items-center gap-3 p-3 rounded-lg border bg-white text-sm">
          <Users className="h-4 w-4 text-muted-foreground shrink-0" />
          <div className="flex-1"><span className="font-medium">{c.name}</span>{c.role && <Badge variant="outline" className="ml-2 text-[8px]">{c.role}</Badge>}<div className="flex gap-3 mt-0.5 text-xs text-muted-foreground">{c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}{c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}</div></div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(c.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
        </div>
      ))}</div>
      <Dialog open={addOpen} onOpenChange={setAddOpen}><DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Add Contact</DialogTitle></DialogHeader><div className="space-y-3 py-2">
        <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs">Email</Label><Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="mt-1" /></div><div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1" /></div></div>
        <div><Label className="text-xs">Role</Label><Input value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} placeholder="Account Manager, Support Lead..." className="mt-1" /></div>
      </div><DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.name}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Add</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}

// ── Training ──
function TrainingTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ product_id: '', title: '', url: '', link_type: 'portal', description: '' });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/vendor-portal/dashboard`, { headers }).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);

  const save = async () => {
    setSaving(true);
    try { await axios.post(`${BACKEND}/api/vendor-portal/training-links`, form, { headers }); toast.success('Training link added'); setAddOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between"><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Training & Documentation</h2><Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> Add Link</Button></div>
      <p className="text-sm text-muted-foreground">Add training portals, LMS courses, certifications, and documentation links for your products. These are surfaced to hotel staff via QR support cards.</p>
      <Dialog open={addOpen} onOpenChange={setAddOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Add Training Link</DialogTitle></DialogHeader><div className="space-y-3 py-2">
        <div><Label className="text-xs">Product</Label><Select value={form.product_id || '__none__'} onValueChange={v => setForm(p => ({ ...p, product_id: v === '__none__' ? '' : v }))}><SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{(data?.products || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="StayNTouch Academy" className="mt-1" /></div>
        <div><Label className="text-xs">URL</Label><Input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} placeholder="https://academy.stayntouch.com" className="mt-1" /></div>
        <div><Label className="text-xs">Type</Label><Select value={form.link_type} onValueChange={v => setForm(p => ({ ...p, link_type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="portal">Training Portal</SelectItem><SelectItem value="lms">LMS Course</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="documentation">Documentation</SelectItem><SelectItem value="certification">Certification</SelectItem></SelectContent></Select></div>
      </div><DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.title || !form.url}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Add</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}
